/**
 * Der laute Rückfall von `QuestionField`.
 *
 * Bis hierher endete jeder nicht behandelte Feldtyp in einem nackten
 * `<input type="text">` — grün im type-check, grün im Build, falsch im Browser.
 * Ein Kunde tippte dann Text in ein Feld, das eine Datei erwartet, und niemand
 * bemerkte es: weder er, noch der Berater, noch das Log.
 *
 * Was stattdessen passiert:
 *  - **sichtbar** statt still: eine Meldung an der Stelle des Feldes,
 *  - **auffindbar** im Betrieb: `console.error` mit festem Präfix (einmal je
 *    Renderer und Feldtyp, nicht bei jedem Rendern),
 *  - **ohne Absturz**: kein `throw`, das Formular bleibt bedienbar und
 *    absendbar,
 *  - **ohne Datenverlust**: der gespeicherte Wert wird nicht angefasst — es
 *    gibt kein Eingabefeld, das ihn überschreiben könnte.
 */
import { makeStyles, shorthands } from "@fluentui/react-components";
import { Warning20Filled } from "@fluentui/react-icons";
import * as React from "react";
import {
  reportUnknownFieldType,
  UNKNOWN_FIELD_TYPE_ATTR,
  unknownFieldTypeMessage,
} from "@kmvs/forms-core";
import type { FieldDef } from "@kmvs/forms-core";
import { BRAND, REFINED } from "./tokens";

const useStyles = makeStyles({
  kasten: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    lineHeight: 1.4,
    color: REFINED.goldInk,
    backgroundColor: REFINED.goldBg,
    ...shorthands.padding("10px", "12px"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", REFINED.goldLine),
  },
  symbol: {
    flexShrink: 0,
    marginTop: "1px",
  },
});

const UnbekannterFeldtyp = ({ field, renderer }: { field: FieldDef; renderer: string }) => {
  const styles = useStyles();
  // Im Effekt, nicht im Rumpf: unter React 18 StrictMode läuft der Rumpf
  // doppelt, und ein Renderfehler ist ohnehin kein Grund, das Rendern selbst
  // mit einer Konsolenausgabe zu belasten.
  React.useEffect(() => {
    reportUnknownFieldType(renderer, field);
  }, [renderer, field]);

  return (
    <div
      className={styles.kasten}
      role="alert"
      {...{ [UNKNOWN_FIELD_TYPE_ATTR]: String(field.type) }}
    >
      <Warning20Filled className={styles.symbol} />
      <span>{unknownFieldTypeMessage(field)}</span>
    </div>
  );
};

export default UnbekannterFeldtyp;
